import { createContext, useContext, useState, useEffect } from 'react'

import { FieldValues } from 'react-hook-form'
import { DataItem } from 'scrivito'

import { DocumentsUploadOptions } from './uploadDocuments'
import { useDocumentFolderNavigation } from './useDocumentFolderNavigation'
import { useDocumentLoader } from './useDocumentLoader'
import { useDocumentOperations } from './useDocumentOperations'
import {
  DocumentRelationType,
  FolderStackItem,
  DocumentsFilters,
} from '@/Components/Document/types'
import { DataClassName, DOCUMENT_DESC_ORDER } from '@/Data/const'
import { DataScopeOrder } from '@/Data/types'

const INITIAL_FILTER: DocumentsFilters = {
  type: { title: '', value: '' },
}

interface DocumentContextProps {
  documents: DataItem[]
  folderStack: FolderStackItem[]
  createFolder: (formData: FieldValues) => Promise<void>
  openFolder: (folderId: string) => void
  goBackFolder: () => void
  goToFolder: (folderId: string) => void
  goToRoot: () => void
  deleteRelation: (documentId: string) => Promise<void>
  deleteFolder: (folderId: string) => Promise<void>
  uploadDocuments: (
    files: File[],
    opts?: DocumentsUploadOptions,
  ) => Promise<void>
  error: unknown
  order: DataScopeOrder
  setOrder: React.Dispatch<React.SetStateAction<DataScopeOrder>>
  filters: DocumentsFilters
  setFilters: React.Dispatch<React.SetStateAction<DocumentsFilters>>
  count: number
  setSearch: React.Dispatch<React.SetStateAction<string>>
  showNewFolderButton: boolean
  loadDocumentsForFolder: (folderId: string) => Promise<DataItem[] | undefined>
}

const DocumentContext = createContext<DocumentContextProps | null>(null)

export const useDocumentContext = () => {
  const ctx = useContext(DocumentContext)
  if (!ctx) {
    throw new Error('useDocumentContext must be used within a DocumentProvider')
  }
  return ctx
}

export const DocumentProvider = ({
  entity,
  relationTo,
  children,
}: {
  entity?: DataItem
  relationTo?: DocumentRelationType
  children: React.ReactNode
}) => {
  const entityId = entity?.id() ?? null
  const [documents, setDocuments] = useState<DataItem[]>([])
  const [folderStack, setFolderStack] = useState<FolderStackItem[]>([])
  const [error, setError] = useState<unknown>(null)
  const [order, setOrder] = useState<DataScopeOrder>(DOCUMENT_DESC_ORDER)
  const [filters, setFilters] = useState<DocumentsFilters>(INITIAL_FILTER)
  const [count, setCount] = useState(0)
  const [search, setSearch] = useState('')
  const limit = 999
  // hide new folder button in activities
  const showNewFolderButton = entity?.dataClassName() !== DataClassName.ACTIVITY

  const {
    loadDocumentsForEntity,
    loadDocumentsFromRelation,
    loadDocumentsForFolder,
  } = useDocumentLoader({
    filters,
    order,
    limit,
    search,
    setCount,
    setError,
    entityId,
    relationTo,
    setDocuments,
  })
  const { openFolder, goToRoot, goBackFolder, goToFolder } =
    useDocumentFolderNavigation({
      folderStack,
      setFolderStack,
      filters,
      order,
      limit,
      search,
      setCount,
      setError,
      loadDocumentsFromRelation,
      loadDocumentsForEntity,
    })
  const {
    uploadDocuments,
    createFolder,
    deleteRelation,
    deleteFolder,
    refresh,
  } = useDocumentOperations({
    entityId,
    relationTo,
    folderStack,
    setFolderStack,
    setError,
    openFolder,
    loadDocumentsForEntity,
  })

  useEffect(() => {
    loadDocumentsForEntity()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId])

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, order, limit, search])

  return (
    <DocumentContext.Provider
      value={{
        documents,
        folderStack,
        openFolder,
        goBackFolder,
        goToFolder,
        goToRoot,
        error,
        order,
        setOrder,
        filters,
        setFilters,
        count,
        uploadDocuments,
        setSearch,
        createFolder,
        deleteRelation,
        deleteFolder,
        showNewFolderButton,
        loadDocumentsForFolder,
      }}
    >
      {children}
    </DocumentContext.Provider>
  )
}
